/**
 * The system error log's pure half (piece 9; decisions 0822 and 0823; T271).
 * 🔒 A row is sanitised when it is written, never at export: the API runs every
 * message and stack through `sanitizeMessage` and `sanitizeStack` before the
 * insert, so what is stored is already what an export carries. The fingerprint
 * and the repair bundle are built here too, so the server and a test read one
 * copy of each rule.
 *
 * 💰 The redaction is a pattern pass and misses what it was not shown: a
 * customer's name the code read from a row and wrote into its own message is not
 * in the request, and stands. The piece says so beside the rule.
 */

/**
 * Where a row came from: the API's refusal filter or a catch (Server), the shell's boundary, rejection listener and
 * catches (Browser), or a refusal the code chose, answered by the server or shown by a client (Refusal; decision 11940).
 */
export const SYSTEM_ERROR_SOURCES = ['Server', 'Browser', 'Refusal'] as const;
export type SystemErrorSource = (typeof SYSTEM_ERROR_SOURCES)[number];

/**
 * 🔒 The Source filter on Settings › System › System errors (piece 15; row T3701). The phone posts through the browser's
 * route, so its rows read source Browser; they are told apart by a route that opens on `/phone` or a user agent that
 * opens on `BMS2-Phone/`, and the filter names them Phone. 🔒 Refusal lists the refusals alone, and no source chosen lists
 * every row but a refusal (decision 11940).
 */
export const SYSTEM_ERROR_SOURCE_FILTERS = ['Server', 'Browser', 'Phone', 'Refusal'] as const;
export type SystemErrorSourceFilter = (typeof SYSTEM_ERROR_SOURCE_FILTERS)[number];
/** What every phone row's route opens on (decision 11620). */
export const PHONE_ERROR_ROUTE_PREFIX = '/phone';
/** What every phone row's user agent opens on (decision 11620). */
export const PHONE_USER_AGENT_PREFIX = 'BMS2-Phone/';

/** The filter value a request names, or null where it names none this page offers. */
export function systemErrorSourceFilterOf(value: unknown): SystemErrorSourceFilter | null {
  return typeof value === 'string' && (SYSTEM_ERROR_SOURCE_FILTERS as readonly string[]).includes(value) ? (value as SystemErrorSourceFilter) : null;
}

/**
 * Which of the four a row reads as: Server, Refusal, Phone (a Browser row on a `/phone` route or from a `BMS2-Phone/` user
 * agent, as the server's Source filter lists it, T3733) or Browser.
 */
export function systemErrorSourceOf(row: { source: SystemErrorSource; route: string; userAgent?: string | null }): SystemErrorSourceFilter {
  if (row.source === 'Server' || row.source === 'Refusal') return row.source;
  const onPhoneRoute = row.route === PHONE_ERROR_ROUTE_PREFIX || row.route.startsWith(`${PHONE_ERROR_ROUTE_PREFIX}/`);
  return onPhoneRoute || (row.userAgent ?? '').startsWith(PHONE_USER_AGENT_PREFIX) ? 'Phone' : 'Browser';
}

/** 🔒 Piece 15 declares the key; past this many open rows in a minute a fingerprint writes a count, not a row. */
export const SYSTEM_ERRORS_PER_MINUTE_KEY = 'systemErrors.perMinuteLimit';
export const SYSTEM_ERRORS_PER_MINUTE_DEFAULT = 10;

/** 🔒 Piece 15 declares the key, seeded true: whether a refusal the code chose writes a row, source Refusal (decision 11940). */
export const SYSTEM_ERRORS_LOG_REFUSALS_KEY = 'systemErrors.logRefusals';
export const SYSTEM_ERRORS_LOG_REFUSALS_DEFAULT = true;

/** The header every answer carries and the browser keeps for its last failed read (decision 0822). */
export const REQUEST_ID_HEADER = 'x-request-id';

/** How much of a fault is kept: enough to repair it, bounded so one fault cannot fill the table. */
export const SYSTEM_ERROR_LIMITS = { message: 1000, frames: 25, frameLength: 300, route: 200, kind: 80, version: 64, userAgent: 300, requestId: 64, candidates: 200, candidateLength: 200 } as const;

/** What the redaction pass replaces beside its patterns: the strings the request carried, and the signed-in person's names. */
export interface RedactionCandidates {
  readonly values?: readonly string[];
  readonly names?: readonly string[];
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
/** A North American or international number: an optional country code, then three, three and four digits in any usual punctuation. */
const PHONE = /(?:\+\d{1,3}[\s.-]?)?(?:\(\d{3}\)\s?|\b\d{3}[\s.-])\d{3}[\s.-]\d{4}\b|\+\d{10,15}\b/g;
/** A card or account number: eight to nineteen digits, spaces or dashes allowed between them. */
const LONG_NUMBER = /\b\d(?:[ -]?\d){7,18}\b/g;
const STREET = /\b\d{1,6}\s+(?:[A-Za-z0-9.'-]+\s+){1,4}(?:street|st|avenue|ave|road|rd|boulevard|blvd|lane|ln|drive|dr|court|ct|way|place|pl|terrace|ter|parkway|pkwy|highway|hwy|circle|cir|square|sq|trail|trl)\b\.?/gi;

/**
 * An id, an ISO date or instant, and a System error's reference where a line names it, each kept whole through the
 * pass. 🔨 A reference is eight hexadecimal digits, and about one in forty is all decimal, which the number pattern
 * would take for an account and the API log could then not link (decision 1528; found in review, 2026-10-02).
 */
const SHIELDED = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?\b|\bSystem error [0-9A-F]{8}\b/gi;
/** A private-use character marks a shielded run; no message carries one. */
const SHIELD = '\uE000';
const SHIELD_BACK = /\uE000(\d+)\uE000/g;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A string worth redacting from the request: three characters or more, not an id, not a bare flag, not a short number. */
function redactable(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 3) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) return false;
  if (/^(true|false|null|undefined)$/i.test(trimmed)) return false;
  if (/^\d{1,7}$/.test(trimmed)) return false;
  return true;
}

/**
 * Every string leaf the request carried, in its body, query and path, for the
 * redaction pass to replace (decision 0822). 🔨 Bounded in count and length, so a
 * large body cannot make the pass itself the fault.
 */
export function requestCandidates(...sources: readonly unknown[]): string[] {
  const out = new Set<string>();
  const walk = (value: unknown, depth: number): void => {
    if (out.size >= SYSTEM_ERROR_LIMITS.candidates || depth > 6) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (redactable(trimmed)) out.add(trimmed.slice(0, SYSTEM_ERROR_LIMITS.candidateLength));
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) walk(item, depth + 1);
      return;
    }
    if (value !== null && typeof value === 'object') for (const item of Object.values(value as Record<string, unknown>)) walk(item, depth + 1);
  };
  for (const source of sources) walk(source, 0);
  return [...out];
}

/**
 * 🔒 The redaction pass (decision 0822): email addresses, phone numbers, card
 * and account numbers, numbered street addresses, every string the request
 * carried and the signed-in person's names. An id is kept, because an id is not
 * a name, and it is shielded from the number pattern first.
 */
export function redact(text: string, candidates: RedactionCandidates = {}): string {
  // An id and a date are not customer data, and each is shielded before the number pattern can read it as an account.
  const kept: string[] = [];
  let out = text.replace(SHIELDED, (found) => {
    kept.push(found);
    return `${SHIELD}${kept.length - 1}${SHIELD}`;
  });
  // Longest first, so a full name goes before the first name inside it.
  const names = [...new Set((candidates.names ?? []).map((n) => n.trim()).filter(redactable))].sort((a, b) => b.length - a.length);
  const values = [...new Set((candidates.values ?? []).map((v) => v.trim()).filter(redactable))].sort((a, b) => b.length - a.length);
  for (const name of names) out = out.replace(new RegExp(`\\b${escapeRegExp(name)}\\b`, 'gi'), '[name]');
  for (const value of values) out = out.replace(new RegExp(escapeRegExp(value), 'gi'), '[value]');
  out = out.replace(EMAIL, '[email]').replace(STREET, '[address]').replace(PHONE, '[phone]').replace(LONG_NUMBER, '[number]');
  return out.replace(SHIELD_BACK, (_, at: string) => kept[Number(at)] ?? '');
}

/**
 * 🔒 The message as stored: a database client's error keeps its last line alone,
 * because the lines above quote the call's arguments; everything else keeps its
 * text, bounded and redacted.
 */
export function sanitizeMessage(errorKind: string, message: string, candidates: RedactionCandidates = {}): string {
  let text = message ?? '';
  if (/^Prisma/.test(errorKind)) {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    text = lines[lines.length - 1] ?? '';
  }
  return redact(text.slice(0, SYSTEM_ERROR_LIMITS.message * 2), candidates).slice(0, SYSTEM_ERROR_LIMITS.message);
}

/** A stack frame line: V8's `at …`, the Firefox and Safari `fn@url:line:col`, or React's component stack. */
function isFrame(line: string): boolean {
  const trimmed = line.trim();
  return /^at\s/.test(trimmed) || /@\S*:\d+(:\d+)?\)?$/.test(trimmed) || /^in\s\S+/.test(trimmed);
}

/**
 * 🔒 Whether a browser fault names nothing of the application's (piece 9; T363;
 * decision 0998): no frame at all, or no frame and no message under the page's own
 * origin. A script the browser or one of its extensions injected into the page
 * throws from a frame of its own, or from none, and is not a fault to repair.
 * 🔨 Read from the stack as reported, before its origins are stripped for storage.
 * A React component frame is the application's own, and an origin nobody reported
 * leaves a stack with frames counted as ours, because nothing says otherwise.
 */
export function isOutsideApplication(message: string, stack: string | null | undefined, appOrigin: string | null | undefined): boolean {
  if (appOrigin && message.includes(appOrigin)) return false;
  const frames = (stack ?? '').split('\n').filter(isFrame).map((line) => line.trim());
  if (frames.length === 0) return true;
  if (frames.some((line) => /^in\s/.test(line))) return false;
  if (!appOrigin) return false;
  return !frames.some((line) => line.includes(appOrigin));
}

/** A page origin as a browser reports it, scheme and host alone, or null where it is not one. */
export function reportedOrigin(value: unknown): string | null {
  return typeof value === 'string' && /^https?:\/\/[^/\s?#]+$/.test(value) ? value : null;
}

/** A URL keeps its path: the origin says nothing about the fault, and a query string is never stored. */
function stripUrl(line: string): string {
  return line.replace(/https?:\/\/[^/\s)]+/g, '').replace(/\?[^\s):]*/g, '');
}

/** 🔒 The stack as stored: frame lines alone, at most twenty-five, origins and query strings gone, redacted. */
export function sanitizeStack(stack: string | null | undefined, candidates: RedactionCandidates = {}): string | null {
  if (!stack) return null;
  const frames = stack.split('\n').filter(isFrame).slice(0, SYSTEM_ERROR_LIMITS.frames).map((line) => redact(stripUrl(line.trim()).slice(0, SYSTEM_ERROR_LIMITS.frameLength), candidates));
  return frames.length === 0 ? null : frames.join('\n');
}

/** 🔒 A path as stored: no query string, no fragment, and every id or number segment replaced (decision 0822). */
export function routePattern(path: string): string {
  const bare = (path ?? '').split(/[?#]/)[0] ?? '';
  const segments = bare.split('/').map((segment) => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':id';
    if (/^\d+$/.test(segment)) return ':n';
    return redact(decodeSafely(segment));
  });
  return segments.join('/').slice(0, SYSTEM_ERROR_LIMITS.route) || '/';
}

function decodeSafely(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The message's shape: numbers, ids and quoted text taken out, so two occurrences of one fault read alike. */
export function messageShape(message: string): string {
  return message
    .replace(UUID, ':id')
    .replace(/(["'`])(?:(?!\1).){0,200}\1/g, '""')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** One frame reduced to what survives a deploy: the function and the file's own name, no line, column, path or bundle hash. */
export function frameShape(line: string): string {
  let text = stripUrl(line.trim()).replace(/^at\s+/, '').replace(/^in\s+/, '');
  text = text.replace(/:\d+(:\d+)?\)?$/, '').replace(/\)$/, '');
  text = text.replace(/\(([^()]*)$/, ' $1').replace(/@/, ' ');
  return text
    .split(/\s+/)
    .map((part) => part.replace(/^.*[\\/]/, '').replace(/[-.][A-Za-z0-9_]{8,}(\.[cm]?js)$/, '$1'))
    .filter(Boolean)
    .join(' ');
}

/** cyrb53: a small, stable, well-spread hash that runs the same in the API and in a browser. */
function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

/**
 * 🔒 The fingerprint: a stable hash of the error's kind, its message's shape and
 * its top five frames (decision 0822). Two stacks that differ only in line
 * numbers, columns, bundle hashes or ids give one fingerprint.
 */
export function fingerprintOf(errorKind: string, message: string, stack: string | null | undefined): string {
  const frames = (stack ?? '').split('\n').filter(isFrame).slice(0, 5).map(frameShape);
  return hash53([errorKind.trim(), messageShape(message), ...frames].join('\n'));
}

/** A class name or `Error`, bounded, never free text. */
export function errorKindOf(value: unknown): string {
  const raw = typeof value === 'string' ? value : '';
  const kind = raw.replace(/[^A-Za-z0-9_$.]/g, '').slice(0, SYSTEM_ERROR_LIMITS.kind);
  return kind || 'Error';
}

/** A version or commit as the build stamped it; anything else reads as unknown. */
export function appVersionOf(value: unknown): string {
  const raw = typeof value === 'string' ? value.trim() : '';
  return /^[A-Za-z0-9._-]{1,64}$/.test(raw) ? raw : 'unknown';
}

/** A request id is ours or nothing: the shape the API writes, never free text a report carried. */
export function requestIdOf(value: unknown): string | null {
  const raw = typeof value === 'string' ? value.trim() : '';
  return /^[A-Za-z0-9-]{8,64}$/.test(raw) ? raw : null;
}

// ---- What the page and the export read ----

/** One fingerprint's group on Settings › System › System Errors (decision 0823). */
export interface SystemErrorGroupRow {
  readonly fingerprint: string;
  readonly errorKind: string;
  readonly message: string;
  readonly source: SystemErrorSource;
  readonly route: string;
  /** Every occurrence: the rows and the repeats folded into them. */
  readonly count: number;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  /**
   * True while any row of the group is unresolved; a recurrence after Resolve is a new open row.
   * 🔒 False for a group outside the application, which the open count leaves out (T363).
   */
  readonly open: boolean;
  readonly latestReference: string;
  /** 🔒 The newest row names nothing of ours: a script the browser or an extension injected (T363; decision 0998). */
  readonly outsideApplication?: boolean;
  /** The newest row's user agent, which labels a row the server lists under Phone by its agent alone (T3733). */
  readonly userAgent?: string | null;
}

/** One stored occurrence, as the group's page lists it. */
export interface SystemErrorOccurrenceRow {
  readonly id: string;
  readonly reference: string;
  readonly occurredAt: string;
  readonly source: SystemErrorSource;
  readonly route: string;
  readonly requestId: string | null;
  readonly appVersion: string;
  readonly repeatCount: number;
  readonly message: string;
  readonly stack: string | null;
  readonly userAgent: string | null;
  readonly resolvedAt: string | null;
}

export interface SystemErrorsAnswer {
  readonly rows: readonly SystemErrorGroupRow[];
}

export interface SystemErrorGroupAnswer {
  readonly group: SystemErrorGroupRow;
  readonly occurrences: readonly SystemErrorOccurrenceRow[];
}

/** What the repair bundle carries per group, and nothing a customer wrote (decision 0823). */
export interface RepairBundleGroup {
  readonly fingerprint: string;
  readonly errorKind: string;
  readonly source: SystemErrorSource;
  readonly route: string;
  readonly count: number;
  readonly rowCount: number;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly open: boolean;
  /** 🔒 The group names nothing of ours (T363; decision 0998). */
  readonly outsideApplication?: boolean;
  readonly versions: readonly string[];
  readonly requestIds: readonly string[];
  readonly message: string;
  readonly stack: string | null;
}

/**
 * 🔒 The repair bundle (decision 0823): one markdown file a person pastes into a
 * repair session, each group written out for reading, and the same facts as a
 * JSON block at the foot for a tool. Instants are UTC.
 */
export function repairBundle(groups: readonly RepairBundleGroup[], meta: { generatedAt: string; from: string | null; to: string | null }): string {
  const fence = '```';
  // A fence inside the text would close the block early; three backticks become three quotes.
  const fenced = (text: string) => text.replace(/```/g, "'''");
  const lines: string[] = [
    '# System errors — repair bundle',
    '',
    `Generated ${meta.generatedAt} (UTC). Range: ${meta.from ?? 'the beginning'} to ${meta.to ?? 'now'}. Groups: ${groups.length}.`,
    '',
    'Every message and stack below was sanitised when it was written: email addresses, phone numbers, long numbers, street addresses, the values the failing request carried and the signed-in person’s names are replaced. Nothing a customer wrote is included.',
    '',
  ];
  for (const group of groups) {
    lines.push(
      `## ${group.errorKind}: ${group.message.split('\n')[0] ?? ''}`,
      '',
      `- Fingerprint: \`${group.fingerprint}\``,
      `- Source: ${group.source}`,
      `- Route: \`${group.route}\``,
      `- Occurrences: ${group.count} (${group.rowCount} stored, ${group.count - group.rowCount} folded by the per-minute limit)`,
      `- First seen: ${group.firstSeenAt}`,
      `- Last seen: ${group.lastSeenAt}`,
      `- Status: ${group.outsideApplication ? 'outside the application: no frame of ours, so a script the browser or an extension injected' : group.open ? 'open' : 'resolved'}`,
      `- Versions: ${group.versions.join(', ') || 'unknown'}`,
      `- Request ids: ${group.requestIds.join(', ') || 'none'}`,
      '',
      'Message:',
      '',
      fence,
      fenced(group.message),
      fence,
      '',
      'Stack:',
      '',
      fence,
      fenced(group.stack ?? '(no stack was recorded)'),
      fence,
      '',
    );
  }
  lines.push('## The same, as JSON', '', `${fence}json`, JSON.stringify({ generatedAt: meta.generatedAt, from: meta.from, to: meta.to, groups }, null, 2), fence, '');
  return lines.join('\n');
}
